import React, { useEffect, useState, useMemo, useCallback } from 'react';
import type { InviteTarget } from '../types';
import { inviteAPI } from '@/api/inviteAPI';

interface ExternalTabProps {
  query: { text: string; version: number };
  selectedUsers: InviteTarget[];
  onToggleUser: (user: InviteTarget) => void;
  onCopyLink: () => void; // 만랩 기획에 링크 복사 UI가 있다면 재확인용, 현재 뷰에선 보이지 않음
}

const ExternalTab: React.FC<ExternalTabProps> = ({ query, selectedUsers, onToggleUser, onCopyLink }) => {
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);
  const [recentEmails, setRecentEmails] = useState<string[]>([]);

  const selectedIds = useMemo(() => new Set(selectedUsers.map(u => u.id)), [selectedUsers]);
  
  // 추가한 이메일 주소: 상단에서 선택된 항목 중 type 이 EMAIL 인 것들
  const addedEmails = useMemo(() => {
    return selectedUsers
      .filter(u => u.type === 'EMAIL')
      .map(u => u.email || u.id);
  }, [selectedUsers]);

  // 이메일 정규식 검사기
  const isValidEmail = (emailStr: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);

  const fetchRecentEmails = useCallback(async () => {
    try {
      const res = await inviteAPI.getRecentInviteEmails();
      if (res?.resultCode === 200 && res.resultData?.emailList) {
        setRecentEmails(res.resultData.emailList);
      }
    } catch (e) {
      console.error('Failed to load recent invite emails', e);
    }
  }, []);

  useEffect(() => {
    fetchRecentEmails();
  }, [fetchRecentEmails]);

  const filteredAddedEmails = useMemo(() => {
    if (!query.text) return addedEmails;
    return addedEmails.filter(e => e.toLowerCase().includes(query.text.toLowerCase()));
  }, [addedEmails, query.text]);

  const filteredRecentEmails = useMemo(() => {
    if (!query.text) return recentEmails;
    return recentEmails.filter(e => e.toLowerCase().includes(query.text.toLowerCase()));
  }, [recentEmails, query.text]);

  const isSearchEmpty = query.text && filteredAddedEmails.length === 0 && filteredRecentEmails.length === 0;

  const handleAddEmail = () => {
    const trimmed = email.trim();
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setShowError(true);
      return;
    }
    
    setShowError(false);
    
    if (!selectedIds.has(trimmed)) {
      onToggleUser({
        id: trimmed,
        type: 'EMAIL',
        name: trimmed,
        email: trimmed
      });
    }

    setEmail('');
  };

  const renderEmailList = (title: string, list: string[], allowEmptyMessage: boolean = false) => {
    if (list.length === 0 && !allowEmptyMessage) return null;
    return (
      <React.Fragment>
        <div className="rtcOrganization__adSection">{title}</div>
        {list.length === 0 && allowEmptyMessage && (
          <div className="rtcOrganization__adItem" style={{ borderBottom: 'none', justifyContent: 'center' }}>
            <span style={{ color: '#999', fontSize: 13, padding: '20px 0' }}>추가된 내역이 없습니다.</span>
          </div>
        )}
        {list.map(emailStr => {
          const isSelected = selectedIds.has(emailStr);
          return (
            <div key={`${title}_${emailStr}`} className="rtcOrganization__adItem">
              <div className="rtcOrganization__adItemLeft">
                <div className="infoBox">
                  <div className="infoBox__name">
                    <em className="ellipsis">{emailStr}</em>
                  </div>
                </div>
              </div>
              <div className="rtcOrganization__adItemRight">
                <div className="common_radio_input type_agree5 rtcNewType">
                  <label>
                    <input 
                      className="radio_input" 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={() => onToggleUser({
                        id: emailStr,
                        type: 'EMAIL',
                        name: emailStr,
                        email: emailStr
                      })}
                    />
                    <span className="icon"></span>
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <>
      {/* 링크 공유 버튼은 3탭 기획상 빠져있으나 prop으로 예약됨. 필요 시 상단 배너 배치 가능 */}
      <div className={"rtcOrganization_adSearch "+(showError ? "error" : "")}>
        <div style={{ position: 'relative', width: '100%' }}>
          <input 
            type="text" 
            className={`rtcOrganization_adSearch_inp ${showError ? 'is-error' : ''}`} 
            style={{width:"100%"}}
            placeholder="이메일 주소를 입력해주세요" 
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (showError) setShowError(false);
            }}
            onKeyDown={(e) => { if(e.key === 'Enter') handleAddEmail(); }}
          />
        </div>
        <button 
          type="button" 
          className="rtcOrganization_adSearch_text"
          style={{ color: (email.trim() && isValidEmail(email.trim())) ? '#1c90fb' : '' }}
          onClick={handleAddEmail}
        >
          추가
        </button>
      </div>

      {showError && (
        <p className="rtcOrganization_adSearch__errorText">올바르지 않은 이메일 주소입니다.</p>
      )}

      <div className="rtcOrganization__top scrollCustom">
        {isSearchEmpty ? (
          <div className="rtcNew_joinMemberListWrap chatbox--empty">
            <div className="empty_area">
              <span className="ico"><img src="https://static.wehago.com/imgs/common/ico_nodata138.png" width="42" alt="" /></span>
              <p className="empty_txt">찾으시는 참석자가 없어요.<br />이름을 다시 확인해주세요.</p>
            </div>
          </div>
        ) : (
          <>
            {renderEmailList('추가한 이메일 주소', filteredAddedEmails, true)}
            {renderEmailList('최근 초대한 이메일 주소', filteredRecentEmails)}
          </>
        )}
      </div>
    </>
  );
};

export default ExternalTab;
